import { useState, useMemo } from "react";
import { Box, BoxHeader, BoxTitle, BoxNavRight, BoxContent } from "../Box";
import Stack from "../Stack";
import { IconButton } from "../Button";
import { TrashCan, ChevronDown } from "../Icons";
import { useFields } from "../../hooks/use-fields";
import Input from "../Input";
import { useSkipioApi } from "../../api";
import { transformKey, caseMethods } from "../../api/case";
import useLoadAll from "../../hooks/use-load-all";
import { useSkipioQuery } from "../../hooks/use-query";

function snakeCaseTopLevel(object) {
  const clone = {};
  for (let [key, value] of Object.entries(object)) {
    clone[transformKey(key, caseMethods.snake)] = value;
  }
  return clone;
}

function Condition({ value, onChange, onRemove }) {
  const { field, value: condition } = useFields({ onChange, value });
  const [isExpanded, setIsExpanded] = useState(!condition.id);
  const skipioApi = useSkipioApi();
  const automationMeta = useSkipioQuery({
    queryKey: ["automations", "meta"],
  }).data;

  const conditions = useMemo(
    () => snakeCaseTopLevel(automationMeta.conditions),
    [automationMeta]
  );

  const operators = useMemo(() => snakeCaseTopLevel(conditions.operators), [
    conditions,
  ]);

  const { data: contactFields = [] } = useLoadAll(skipioApi.contactFields.get);

  // Get currently selected items
  const contactField = contactFields.find(
    (cf) => cf.id === condition.contactFieldId
  );

  const [, operator] =
    Object.entries(operators).find(([key]) => key === condition.operator) || [];

  const textExplanationValue = !operator
    ? ""
    : (
        {
          string: () => `"${condition.value}"`,
          numeric: () => condition.value,
          boolean: () => (condition.value ? "yes" : "no"),
          date: () => `${condition.value} days`,
        }[contactField ? contactField.fieldType : ""] || (() => null)
      )();

  return (
    <Box>
      <BoxHeader>
        <BoxTitle>
          {!contactField
            ? "Condition"
            : `If ${contactField.label}${
                !operator ? "..." : ` ${operator.label} ${textExplanationValue}`
              }`}
        </BoxTitle>

        <BoxNavRight style={{ paddingRight: "8px" }}>
          <Stack space={1} horizontal>
            {Boolean(onRemove) && (
              <IconButton onClick={onRemove}>
                <TrashCan />
              </IconButton>
            )}
            <IconButton onClick={() => setIsExpanded(!isExpanded)}>
              <ChevronDown
                style={{
                  transform: `rotate(${isExpanded ? "0" : "-90"}deg)`,
                }}
              />
            </IconButton>
          </Stack>
        </BoxNavRight>
      </BoxHeader>
      {isExpanded && (
        <BoxContent>
          <Stack>
            <Input type="select" label="If" {...field("contactFieldId")}>
              <option></option>
              {Object.entries(
                contactFields.reduce(
                  (result, item) => ({
                    ...result,
                    [item.remoteSource]: [
                      ...(result[item.remoteSource] || []),
                      item,
                    ],
                  }),
                  {}
                )
              ).map(([label, items]) => {
                return label !== "undefined" ? (
                  <optgroup label={label} key={label}>
                    {items.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.label}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  items.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))
                );
              })}
            </Input>
            <Stack horizontal>
              {Boolean(condition.contactFieldId) && (
                <Input type="select" {...field("operator")} label="Is">
                  <option value=""></option>
                  {Object.entries(operators)
                    .filter(([_, { appliesTo }]) => {
                      const type = contactField ? contactField.fieldType : null;
                      return appliesTo.includes(type);
                    })
                    .map(([key, { label }]) => (
                      <option value={key} key={key}>
                        {label}
                      </option>
                    ))}
                </Input>
              )}
              {Boolean(operator) &&
                (
                  {
                    string: () => (
                      <>
                        <Input
                          {...field("value")}
                          list={contactField.id}
                          label="Value"
                        />
                        <datalist id={contactField.id}>
                          {contactField.contactFieldValues.map(
                            (contactFieldValue) => (
                              <option
                                value={contactFieldValue}
                                key={contactFieldValue}
                              />
                            )
                          )}
                        </datalist>
                      </>
                    ),
                    numeric: () => (
                      <Input
                        type="number"
                        {...field("value", 0)}
                        label="Number"
                      />
                    ),
                    boolean: () => (
                      <Input
                        type="select"
                        onChange={(value) =>
                          field("value").onChange(value === "true")
                        }
                        value={String(field("value").value)}
                        label="Yes or No"
                      >
                        <option></option>
                        <option value="true">yes</option>
                        <option value="false">no</option>
                      </Input>
                    ),
                    date: () => (
                      <Input
                        type="number"
                        {...field("value")}
                        label="Number of Days"
                      />
                    ),
                  }[contactField ? contactField.fieldType : ""] || (() => null)
                )()}
            </Stack>
          </Stack>
        </BoxContent>
      )}
    </Box>
  );
}

export default Condition;
